import classNames from 'classnames';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

import '../../../scss/live-event/base/general-ui/new-dropdown.scss';
import { OptionalComponent } from '../../../utils/optional-component';

interface NewDropdownProps {
	children: any;
	renderTitle: (props: { isOpen: boolean }) => JSX.Element | string;
	className?: string;
	popupClassName?: string;
	valueClassName?: string;
	edge?: 'left' | 'right';
	showArrow?: boolean;
	template?: string;
	isNoScroll?: boolean;
	animateDropdown?: boolean;
}
export default function NewDropdown({
	children,
	renderTitle,
	className,
	valueClassName,
	popupClassName,
	edge = 'left',
	showArrow = true,
	template,
	isNoScroll = true,
	animateDropdown = false,
}: NewDropdownProps): JSX.Element {
	const [isOpen, setOpen] = useState(false);
	const self = useRef<HTMLButtonElement>(null);

	// on escape, close dropdown
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.code === 'Escape' || event.key === 'Escape') {
				setOpen(false);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, []);

	const handleClose = (e: any) => {
		const path = e.path || e.composedPath();
		for (const item of path) {
			if (self.current === item) {
				return;
			}
		}
		setOpen(false);
	};

	useEffect(() => {
		if (isOpen) {
			isNoScroll && document.body.classList.add('modal-open');
			window.addEventListener('click', handleClose); // using mouseup will prevent any children from being clickable
		}
		return () => {
			isNoScroll && document.body.classList.remove('modal-open');
			window.removeEventListener('click', handleClose); // using mouseup will prevent any children from being clickable
		};
	}, [isOpen, isNoScroll]);

	const handleClick = () => {
		setOpen((prev) => !prev);
	};

	return (
		<div
			className={classNames('new-dropdown', className, template, {
				open: isOpen,
			})}
		>
			<button
				onClick={handleClick}
				className={classNames('new-dropdown-value-container', valueClassName)}
				ref={self}
			>
				<span className="new-dropdown-value">{renderTitle({ isOpen })}</span>
				{showArrow && <span className="new-dropdown-icon"></span>}
			</button>
			<OptionalComponent display={isOpen}>
				{animateDropdown ? (
					<>
						<div className="new-dropdown-panel-background" />
						<AnimatePresence>
							<LazyMotion features={domAnimation}>
								<m.div
									initial={{ translateY: -100, opacity: 0 }}
									animate={{ translateY: 0, opacity: 1 }}
									exit={{ translateY: -100, opacity: 0 }}
									style={{ zIndex: 999 }}
									className={classNames('new-dropdown-panel', popupClassName, edge)}
								>
									{children}
								</m.div>
							</LazyMotion>
						</AnimatePresence>
					</>
				) : (
					<>
						<div className="new-dropdown-panel-background" />
						<div className={classNames('new-dropdown-panel', popupClassName, edge)}>
							{children}
						</div>
					</>
				)}
			</OptionalComponent>
		</div>
	);
}
